import React from 'react';
import { CheckCircle2, Clock3, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import DinacharyaLotusIcon from './DinacharyaLotusIcon';

type DinacharyaCTACardProps = {
  currentDate: string;
  todayDate: string;
  tomorrowDate: string;
  generatedDates?: Set<string> | string[];
  isGenerating?: boolean;
  disabled?: boolean;
  onSelectDate?: (isoDate: string) => void;
  onGenerate: () => void;
};

const formatDateLabel = (isoDate: string, locale?: string) => {
  if (!isoDate) return 'selected day';
  const parsed = new Date(`${isoDate}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return isoDate;
  return parsed.toLocaleDateString(locale || undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const DinacharyaCTACard: React.FC<DinacharyaCTACardProps> = ({
  currentDate,
  todayDate,
  tomorrowDate,
  generatedDates,
  isGenerating = false,
  disabled = false,
  onSelectDate,
  onGenerate,
}) => {
  const { t, i18n } = useTranslation('chat');
  const generatedDateSet = generatedDates instanceof Set
    ? generatedDates
    : new Set(generatedDates || []);
  const selectedDayLabel = t('chat.dinacharya.cta.selected_day', 'selected day');
  const currentDateLabel = currentDate ? formatDateLabel(currentDate, i18n.language) : selectedDayLabel;
  const selectedRelative = currentDate === todayDate
    ? t('chat.dinacharya.cta.today', 'Today')
    : currentDate === tomorrowDate
      ? t('chat.dinacharya.cta.tomorrow', 'Tomorrow')
      : t('chat.dinacharya.cta.selected', 'Selected day');
  const isCurrentGenerated = Boolean(currentDate && generatedDateSet.has(currentDate));
  const description = currentDate === tomorrowDate
    ? t(
      'chat.dinacharya.cta.description_tomorrow',
      "Personalized timing, food, work, prayer, rest, and remedies - based on your birth chart and tomorrow's Panchanga."
    )
    : currentDate === todayDate
      ? t(
        'chat.dinacharya.cta.description_today',
        "Personalized timing, food, work, prayer, rest, and remedies - based on your birth chart and today's Panchanga."
      )
      : t(
        'chat.dinacharya.cta.description_selected',
        "Personalized timing, food, work, prayer, rest, and remedies - based on your birth chart and the selected day's Panchanga."
      );

  const renderDateToggle = (isoDate: string, label: string) => {
    const isSelected = currentDate === isoDate;
    const isGenerated = generatedDateSet.has(isoDate);
    return (
      <button
        key={isoDate}
        type="button"
        onClick={() => onSelectDate?.(isoDate)}
        disabled={!onSelectDate || isGenerating || disabled}
        className={[
          'inline-flex min-h-[32px] flex-1 items-center justify-center gap-1.5 rounded-md px-3 text-xs font-semibold transition',
          isSelected
            ? 'bg-gray-900 text-white shadow-sm'
            : 'text-gray-700 hover:bg-white/80',
          (!onSelectDate || isGenerating || disabled) ? 'cursor-default opacity-60' : '',
        ].join(' ')}
        aria-pressed={isSelected}
      >
        {label}
        {isGenerated && <CheckCircle2 className="h-3.5 w-3.5" />}
      </button>
    );
  };

  return (
    <div className="sl-dinacharya-cta mb-5 rounded-xl border border-amber-200 bg-gradient-to-r from-amber-50 to-white px-4 py-4 shadow-sm">
      <div className="sl-dinacharya-cta-toggle-wrapper mb-3 inline-flex w-full max-w-xs rounded-lg border border-amber-200 bg-white/60 p-1">
        {renderDateToggle(todayDate, t('chat.dinacharya.cta.toggle_today', 'Today'))}
        {renderDateToggle(tomorrowDate, t('chat.dinacharya.cta.toggle_tomorrow', 'Tomorrow'))}
      </div>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-amber-700">
            {t('chat.dinacharya.cta.eyebrow', 'DINACHARYA')}
          </div>
          <div className="mt-1 flex items-center gap-2 text-sm font-semibold text-gray-900">
            <DinacharyaLotusIcon className="h-5 w-5 text-amber-700" />
            {t('chat.dinacharya.cta.routine_title', 'Your Daily Vedic Routine')}
          </div>
          <div className="mt-1 flex items-center gap-1.5 text-xs text-gray-600">
            <Clock3 className="h-3.5 w-3.5" />
            {t('chat.dinacharya.cta.date_line', 'for {{date}} ({{relative}})', {
              date: currentDateLabel,
              relative: selectedRelative,
            })}
          </div>
          <p className="mt-2 max-w-xl text-xs leading-relaxed text-gray-600">
            {description}
          </p>
          {isCurrentGenerated && (
            <div className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-800">
              <CheckCircle2 className="h-3.5 w-3.5" />
              {t('chat.dinacharya.cta.generated_for', 'Dinacharya generated for {{date}}', {
                date: currentDateLabel,
              })}
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={onGenerate}
          disabled={disabled || isGenerating || isCurrentGenerated}
          title={t(
            'chat.dinacharya.cta.tooltip',
            'Personal daily routine grounded in your chart - a shastra-grounded chronobiology.'
          )}
          className="inline-flex h-10 shrink-0 items-center justify-center gap-2 rounded-lg bg-gray-900 px-4 text-sm font-semibold text-white shadow-sm transition hover:bg-gray-800 disabled:cursor-not-allowed disabled:bg-gray-300 disabled:text-gray-600"
        >
          {isGenerating ? <Loader2 className="h-4 w-4 animate-spin" /> : <DinacharyaLotusIcon className="h-5 w-5" />}
          {isGenerating
            ? t('chat.dinacharya.cta.generating', 'Generating')
            : isCurrentGenerated
              ? t('chat.dinacharya.cta.generated', 'Generated')
              : t('chat.dinacharya.cta.generate', 'Generate routine')}
        </button>
      </div>
    </div>
  );
};

export default DinacharyaCTACard;
